import {
    DEFAULT_ALERT_MODAL_ID,
    DEFAULT_CONFIRM_MODAL_ID,
    DEFAULT_NUMBER_LOCALE,
    DEFAULT_PHONE_COUNTRY_CODE,
    REFRESH_TOKEN_INTERVAL_TIME,
    SOFT_STATUS_KEY_LENGTH,
    VALUE_TYPE_ISO_STRING,
    WEBSITES,
} from '@/constants/config';
import { CURRENCY, CURRENCY_LOCALES } from '@/constants/currency';
import { REGEX, REGEX_WWW } from '@/constants/regex';
import { STORAGE_KEY } from '@/constants/storage';
import i18nLocales from '@/i18n/locales';
import {
    cookieParse,
    decodeBase64,
    encodeBase64,
    generateContinueUrl,
    getContinueRoute,
    prepareRouteToLogin,
    replaceSubstrings,
} from '@/utils/utils';
import psl from '@theboys2023/1byte-psl';
import * as postalCodes from 'alive-postal-code';
import axios from 'axios';
import b64toBlob from 'b64-to-blob';
import bytes from 'bytes';
import { saveAs } from 'file-saver';
import he from 'he';
import parsePhoneNumber from 'libphonenumber-js';
import floor from 'lodash/floor';
import round from 'lodash/round';
import { isMobile } from 'mobile-device-detect';
import moment from 'moment-timezone';
import { nanoid } from 'nanoid';
import { count } from 'sms-length';
import isPostalCode, { locales } from 'validator/lib/isPostalCode';
import validate from 'vuejs-validators';

const DOMAIN_LENGTH = 70;

export default (app) => {
    return {
        handleThrowError(e) {
            const messageCode = e.response?.data?.messageCode || 'BAD_REQUEST';
            const message = app.$te('error.' + messageCode)
                ? app.$t('error.' + messageCode)
                : app.$t('error.BAD_REQUEST');
            app.$store.commit('message/setMessage', {
                type: 'danger',
                message: message,
            });
        },
        sendingButton(button, text = '') {
            button.innerHTML =
                '<i class="far fa-spinner fa-spin mr-2"></i>' + text;
            button.setAttribute('disabled', 'disabled');
        },
        sentButton(button, text = '') {
            button.innerHTML = text;
            button.removeAttribute('disabled');
        },
        alertConfirm(message, config, callback, id = DEFAULT_CONFIRM_MODAL_ID) {
            const conf = {
                title: 'Are you sure?',
                contentClass: 'modal-confirm',
                buttonSize: 'lg',
                okVariant: 'primary',
                okTitle: app.$i18n.t('common.yes'),
                cancelTitle: app.$i18n.t('common.no'),
                cancelVariant: 'outline-navy-blue',
                hideHeaderClose: false,
                centered: true,
                bodyClass: 'text-grey',
                id,
                ...config,
            };
            app.$bvModal.msgBoxConfirm(message, conf).then((result) => {
                callback(result);
            });
        },
        alertModal(message, config, id = DEFAULT_ALERT_MODAL_ID) {
            const conf = {
                title: app.$i18n.t('common.warning'),
                contentClass: 'modal-alert',
                buttonSize: 'lg',
                okVariant: 'primary',
                hideHeaderClose: false,
                centered: true,
                bodyClass: 'text-grey',
                id,
                ...config,
            };
            app.$bvModal.msgBoxOk(message, conf);
        },
        queryBuilder(object) {
            let queryObject = {};
            Object.keys(object).forEach((i) => {
                if (object[i] || object[i].length > 0) {
                    if (typeof object[i] === 'object' && object[i].length > 0) {
                        queryObject[i] = object[i].map((i) => i).toString();
                    } else {
                        queryObject[i] = object[i];
                    }
                }
            });
            return queryObject;
        },
        isEmptyObj(obj) {
            return Object.keys(obj).length === 0;
        },
        getParamArraySearch(param) {
            if (!param || param.length === 0) return '';
            return param.split(',');
        },
        getParamsFilter(filter, page) {
            let queryObject = {};
            Object.keys(filter).forEach((i) => {
                if (filter[i]) {
                    if (typeof filter[i] === 'object' && filter[i].length) {
                        queryObject[i] = filter[i].map((i) => i).toString();
                    } else {
                        queryObject[i] = filter[i];
                    }
                }
            });
            queryObject.page = page;
            return queryObject;
        },
        getDomain(sld, tld) {
            return sld + '.' + tld;
        },
        generateName(firstName, lastName) {
            return firstName + ' ' + lastName;
        },
        getNumberInString(str) {
            let numb = str.match(/\d/g);
            return numb.join('');
        },
        numberOnly(value) {
            return value.replace(/\D/g, '');
        },
        formatPhone(phone) {
            return parsePhoneNumber('+' + phone);
        },
        checkInvalidPhoneCam(phone, countryCode = DEFAULT_PHONE_COUNTRY_CODE) {
            const parsedPhone = parsePhoneNumber(phone, countryCode);
            return (
                parsedPhone &&
                parsedPhone.isValid() &&
                parsedPhone.country === DEFAULT_PHONE_COUNTRY_CODE
            );
        },
        getTotalValueInObject(obj) {
            function getSum(total, num) {
                return total + Math.round(num);
            }

            return Object.values(obj).reduce(getSum, 0);
        },
        /** @deprecated */
        formatCurrency(value, minFractionDigits = 2, maxFractionDigits = 4) {
            return Number(value).toLocaleString(DEFAULT_NUMBER_LOCALE, {
                maximumFractionDigits: maxFractionDigits,
                minimumFractionDigits: minFractionDigits,
            });
        },
        formatNumber(value) {
            return Number(value).toLocaleString(DEFAULT_NUMBER_LOCALE); // en-GB
        },
        ignoreAsterisk(value) {
            return value.replace('*', '');
        },
        validUrl(url = '') {
            const pattern = new RegExp(
                '^(https?:\\/\\/)' + // protocol
                    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
                    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
                    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
                    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
                    '(\\#[-a-z\\d_]*)?$',
                'i'
            ); // fragment locator
            return !!pattern.test(url);
        },
        hasHttp(url = '') {
            const pattern = new RegExp('^(http|https)://', 'i');
            return pattern.test(url);
        },
        validEmail(email) {
            const pattern = REGEX.EMAIL;
            return pattern.test(String(email).toLowerCase());
        },
        lowerCaseFormatter(value) {
            return value.toLowerCase();
        },
        searchDomainInput(value) {
            return value
                .toLowerCase()
                .replace(/[^a-z0-9-.]/g, '')
                .replace(/([.-])\1+/g, '$1')
                .replace(/\.(?=-)|-(?=\.)/g, '')
                .slice(0, DOMAIN_LENGTH);
        },
        postalCodeValidate(countryCode, value) {
            return postalCodes.validate(countryCode, value) === true;
        },
        trimForm(form) {
            const newForm = {};
            Object.keys(form).forEach((i) => {
                newForm[i] =
                    typeof form[i] === 'string' ? form[i].trim() : form[i];
            });
            return newForm;
        },
        redirectTopUp(returnUrl = '') {
            let path = '/top-up';
            if (returnUrl) {
                const continueRoute = this.encodeBase64(
                    JSON.stringify({
                        returnUrl,
                    })
                );
                path = `/top-up?continue=${continueRoute}`;
            }
            this.redirectStore(path);
        },
        redirectCloudServerConfig() {
            const endPoint = isMobile
                ? '/cloud?key=cs-baby-cloud-1'
                : '/cloud/config/cs-baby-cloud-1';
            this.redirectStore(endPoint);
        },
        redirectSharedHostingConfig() {
            const endPoint = isMobile
                ? '/hosting/shared?key=sh-lite'
                : '/hosting/shared/config/sh-lite?duration=1&durationType=MONTH';
            this.redirectStore(endPoint);
        },
        redirectWPHostingConfig() {
            const endPoint = isMobile
                ? '/hosting/wordpress?key=wp-basic'
                : '/hosting/wordpress/config/wp-basic?duration=1&durationType=MONTH';
            this.redirectStore(endPoint);
        },
        redirectCloudHostingConfig() {
            const endPoint = isMobile
                ? '/hosting/cloud?key=ch-basic'
                : '/hosting/cloud/config/ch-basic';
            this.redirectStore(endPoint);
        },
        redirectSslConfig() {
            const endPoint = isMobile
                ? '/ssl?key=positivessl'
                : '/ssl/config/positivessl?duration=5&durationType=YEAR';
            this.redirectStore(endPoint);
        },
        redirectStore(path) {
            location.href = this.homeURL(path);
        },
        isObject(value) {
            return value !== null && typeof value === 'object';
        },
        isArray(value) {
            return value !== null && Array.isArray(value);
        },
        isNumber(value) {
            return value !== null && typeof value === 'number';
        },
        formatTimeAgo(value) {
            return moment(value).locale(app.$i18n.locale).fromNow();
        },
        isSubDomain(domain) {
            let validDomain = psl.isValid(domain);
            let isSubdomain = false;
            let isDomain = false;
            if (validDomain) {
                const parseDomain = psl.parse(domain);
                isSubdomain = parseDomain.subdomain !== null;
                isDomain = true;
            }
            return isDomain && isSubdomain;
        },
        isDomain(value) {
            const parsed = psl.parse(value);
            return psl.isValid(value) && !parsed.subdomain;
        },
        validDomain(value, allowSubdomain = true) {
            if (allowSubdomain) {
                return psl.isValid(value);
            }
            const parsed = psl.parse(value);
            return Boolean(parsed.domain && parsed.listed && !parsed.subdomain);
        },
        removeFirstWWW(domain) {
            let result = domain;
            if (domain?.startsWith('www.')) {
                result = domain.replace(REGEX_WWW, '');
            }
            return result;
        },
        resetAllPropertiesObjects(objects) {
            if (!objects) {
                return;
            }
            Object.keys(objects).forEach((i) => {
                objects[i] = this.isArray(objects[i])
                    ? []
                    : this.isNumber(objects[i])
                    ? 0
                    : typeof objects[i] === 'object'
                    ? null
                    : '';
            });
        },
        excludeNegativeNumber(value) {
            return value.replace(REGEX.EXCLUDE_NEGATIVE_NUMBER, '');
        },
        formatAmount(amount, currency) {
            return amount >= 0
                ? `+${this.renderPrice(amount, {
                      currency,
                  })}`
                : this.renderPrice(amount, {
                      currency,
                  });
        },
        futureDate(date) {
            return date > new Date(new Date().setHours(0, 0, 0, 0));
        },
        pastDate(date) {
            return date < new Date(new Date().setHours(0, 0, 0, 0));
        },
        downloadBlob(data, filename, mime, isEncodedBase64 = false) {
            // if data is base64 must to decode
            const blob = isEncodedBase64
                ? b64toBlob(data, mime || 'application/octet-stream')
                : new Blob([data], {
                      type: mime || 'application/octet-stream',
                  });
            // Other browsers
            // Create a link pointing to the ObjectURL containing the blob
            const blobURL = window.URL.createObjectURL(blob);
            const tempLink = document.createElement('a');
            tempLink.style.display = 'none';
            tempLink.href = blobURL;
            tempLink.setAttribute('download', filename);
            // Safari thinks _blank anchor are pop ups. We only want to set _blank
            // target if the browser does not support the HTML5 download attribute.
            // This allows you to download files in desktop safari if pop up blocking
            // is enabled.
            if (typeof tempLink.download === 'undefined') {
                tempLink.setAttribute('target', '_blank');
            }
            document.body.appendChild(tempLink);
            tempLink.click();
            document.body.removeChild(tempLink);
            setTimeout(() => {
                // For Firefox it is necessary to delay revoking the ObjectURL
                window.URL.revokeObjectURL(blobURL);
            }, 100);
        },
        smsCounter(value) {
            return count(value || '');
        },
        escapeRegex(string) {
            return string.replace(/[\\^$*+?.()|[\]{}]/g, '\\$&');
        },
        validateDateSmsSchedule(data) {
            const result = {
                isValid: true,
                message: '',
            };
            const timezone = data.timezone || moment.tz.guess();
            const date = moment.tz(data.date + ' ' + data.time, timezone);
            if (date.isValid()) {
                if (date.isBefore(moment())) {
                    result.isValid = false;
                    result.message = app.$t('message.required_future_date');
                }
            } else {
                result.isValid = false;
                result.message = app.$t('message.required_date');
            }
            return result;
        },
        excludeNegativeNumberOrZero(value) {
            const formatted = value.replace(REGEX.EXCLUDE_NEGATIVE_NUMBER, '');
            return Number(formatted) === 0 ? '' : formatted;
        },
        autoGenDuplicatedName(storageNames, name) {
            let result = name;
            const nameIndexArray = [];
            storageNames.forEach((storageName) => {
                if (storageName.trim() === name.trim()) {
                    nameIndexArray.push(0);
                }
                const checkRegex = storageName.match(/\(\d+\)$/);
                if (checkRegex !== null) {
                    const indexLabel = storageName.slice(checkRegex.index);
                    const nameLabel = storageName.slice(0, checkRegex.index);
                    if (nameLabel.trim() === name.trim()) {
                        nameIndexArray.push(
                            Number(indexLabel.replace(/[()]/g, ''))
                        );
                    }
                }
            });
            if (nameIndexArray.length) {
                result = `${result.trim()} (${
                    Math.max(...nameIndexArray) + 1
                })`;
            }
            return result;
        },
        roundNumber(value, dec = 2) {
            return round(value, dec);
        },
        getBlogEndpointWithLocale() {
            return process.env.VUE_APP_BLOG_URL + app.$i18n.locale + '/';
        },
        deepTrimObject(obj) {
            const newObj = obj;
            for (const prop in obj) {
                let value = obj[prop],
                    type = typeof value;
                if (
                    value != null &&
                    (type === 'string' || type === 'object') &&
                    Object.hasOwn(obj, prop)
                ) {
                    if (type === 'object') {
                        this.deepTrimObject(obj[prop]);
                    } else {
                        newObj[prop] = obj[prop].trim();
                    }
                }
            }
            return newObj;
        },
        validateForm(formData, rules, message, extend = null) {
            const form = validate(formData, rules, message);
            let errors = null;
            if (extend) {
                form.extend(extend);
            }
            if (form.validate().errors().any()) {
                errors = form.errors().all();
                Object.keys(errors).forEach((i) => {
                    if (errors[i]) {
                        errors[i] = errors[i][0];
                    }
                });
            }

            return errors;
        },
        datePickerValueType: VALUE_TYPE_ISO_STRING,

        formatDateTime(dateTime, formatString) {
            return moment(moment.utc(dateTime).local())
                .locale('en')
                .format(formatString);
        },
        prepareCurrencyLocale(
            currencyId = app.$store.getters['website/currentCurrencyId']
        ) {
            return CURRENCY_LOCALES[currencyId];
        },
        renderPrice(
            price,
            formatOptions = {},
            locales = this.prepareCurrencyLocale()
        ) {
            return new Intl.NumberFormat(locales, {
                style: 'currency',
                minimumFractionDigits: 2,
                maximumFractionDigits: 4,
                numberingSystem: 'latn',
                currency: CURRENCY,
                ...formatOptions,
            }).format(+price);
        },
        inputServerName(input) {
            return input?.replace(REGEX.SERVER_NAME_REGEX, '');
        },
        encodeBase64,
        decodeBase64,
        getEncodedCurrentRoute() {
            const { params, query, name } = app.$router.currentRoute;
            return this.encodeBase64(
                JSON.stringify({
                    name: name,
                    params,
                    query,
                })
            );
        },
        replaceSubstrings,
        redirectToContinueRoute(queryParams = {}, defaultRouteName = '') {
            const continuePath = app.$router.currentRoute?.query?.continueUrl;
            if (continuePath) {
                const { name, params, query } = JSON.parse(
                    this.decodeBase64(continuePath)
                );
                app.$router
                    .push({ name, params, query: { ...query, ...queryParams } })
                    .catch(() => {});
            } else {
                app.$router.push({ name: defaultRouteName }).catch(() => {});
            }
        },
        isPostalCode(str, locale, localeList = locales) {
            return !localeList.includes(locale) || isPostalCode(str, locale);
        },
        homeURL(path = '') {
            const country =
                app.$store.getters['website/currentWebsiteId'] ||
                app.$store.getters['guest/country'];
            const websiteId =
                Object.values(WEBSITES).find((i) => i === country) ||
                WEBSITES.KH;
            const availableLocales = this.getAvailableLocaleCodes(websiteId);
            const locale =
                availableLocales.find((i) => i === app.$i18n.locale) || '';
            const url =
                process.env[`VUE_APP_${websiteId}_SITE`] ||
                process.env.VUE_APP_KH_SITE;
            return url + (locale && locale !== 'en' ? '/' + locale : '') + path;
        },
        roundDownNumber(num, fractionDigit = 2) {
            return floor(num, fractionDigit);
        },
        formatBalance(balance, currency) {
            return this.renderPrice(this.roundDownNumber(balance), {
                currency,
            });
        },
        setRelatedDate(value, filter, relatedField, relatedType) {
            if (
                !filter[relatedField] &&
                value &&
                this.datePickerValueType[relatedType]
            ) {
                filter[relatedField] = app
                    .$moment(value)
                    .format(this.datePickerValueType[relatedType]);
            }
        },
        getParamArray(param) {
            if (param) {
                if (param.length === 0) {
                    return '';
                }
                return param.split(',');
            }
            return '';
        },
        objectFilter(obj, predicate) {
            return Object.keys(obj)
                .filter((key) => predicate(obj[key]))
                .reduce((res, key) => ((res[key] = obj[key]), res), {});
        },
        onlyPositive(value) {
            return value.replace(REGEX.NUMBER, '');
        },
        beforeToday(date) {
            return date > new Date(new Date().setHours(0, 0, 0, 0));
        },
        previousToday(date) {
            return date < new Date(new Date().setHours(0, 0, 0, 0));
        },
        sortByArray(destinationArr, sorterArr, sortBy = null) {
            const reversedSorterArr = [...sorterArr].reverse();
            return destinationArr.sort((a, b) => {
                if (sortBy) {
                    return (
                        reversedSorterArr.indexOf(b[sortBy]) -
                        reversedSorterArr.indexOf(a[sortBy])
                    );
                } else {
                    return (
                        reversedSorterArr.indexOf(b) -
                        reversedSorterArr.indexOf(a)
                    );
                }
            });
        },
        assignInitFromQuery(fromQuery, query) {
            const data = fromQuery;
            if (!fromQuery || this.isEmptyObj(fromQuery)) {
                return data;
            }
            for (const prop in fromQuery) {
                if (fromQuery[prop] != null && Object.hasOwn(fromQuery, prop)) {
                    data[prop] = this.isArray(data[prop])
                        ? this.getParamArray(query[prop]) || []
                        : this.isNumber(data[prop])
                        ? query[prop] || 0
                        : query[prop] || '';
                }
            }
            return data;
        },
        stringToColor(string, opts) {
            let h, s, l;
            opts = opts || {};
            opts.hue = opts.hue || [0, 360];
            opts.sat = opts.sat || [69, 100];
            opts.lit = opts.lit || [40, 60];

            const range = function (hash, min, max) {
                const diff = max - min;
                const x = ((hash % diff) + diff) % diff;
                return x + min;
            };

            let hash = 0;
            if (string.length === 0) return hash;
            for (let i = 0; i < string.length; i++) {
                hash = string.charCodeAt(i) + ((hash << 5) - hash);
                hash = hash & hash;
            }

            h = range(hash, opts.hue[0], opts.hue[1]);
            s = range(hash, opts.sat[0], opts.sat[1]);
            l = range(hash, opts.lit[0], opts.lit[1]);

            return `hsl(${h}, ${s}%, ${l}%)`;
        },
        getFileNameFromUrl(url, defaultName = null) {
            if (defaultName === null || defaultName === undefined) {
                defaultName = new Date().getTime().toString();
            }
            try {
                const urlObj = new URL(url);
                const pathNames = urlObj.pathname.split('/');
                return (
                    (pathNames.length &&
                        decodeURIComponent(pathNames[pathNames.length - 1])) ||
                    defaultName
                );
            } catch (e) {
                return defaultName;
            }
        },
        downloadFileFromUrl(url, name = null) {
            if (!name) {
                name = this.getFileNameFromUrl(url);
            }
            return new Promise((resolve, reject) => {
                axios
                    .get(url, { responseType: 'blob' })
                    .then((response) => {
                        saveAs(response.data, name);
                        resolve();
                    })
                    .catch((e) => {
                        reject(e);
                    });
            });
        },
        formatIntegerNumber(value, minimumIntegerDigits = 1) {
            return Number(value).toLocaleString(DEFAULT_NUMBER_LOCALE, {
                minimumIntegerDigits: minimumIntegerDigits,
                useGrouping: false,
            });
        },
        leadingZeroNumber(number) {
            return String(number).padStart(2, '0');
        },
        getAvailableLocales(websiteId = WEBSITES.KH) {
            return i18nLocales.filter((i) =>
                i.supportCountries.includes(websiteId)
            );
        },
        getAvailableLocaleCodes(websiteId = WEBSITES.KH) {
            return this.getAvailableLocales(websiteId).map((e) => e.code);
        },
        leaveLoginPage() {
            const { continueUrl, continueUrlName } = getContinueRoute(
                app.$route
            );
            if (continueUrl && continueUrlName !== 'login') {
                this.redirectToContinueRoute();
            } else {
                app.$router
                    .push({
                        name: 'dashboard',
                    })
                    .catch(() => {});
            }
        },
        generateContinueUrl,
        getContinueRoute,
        cookieParse,
        refreshTokenInterval: null,
        refreshToken() {
            clearInterval(this.refreshTokenInterval);
            this.refreshTokenInterval = setInterval(async () => {
                const refreshToken =
                    app.$store.state.auth.refreshToken ||
                    cookieParse(app.$cookies.get(STORAGE_KEY.REFRESH_TOKEN));
                if (refreshToken) {
                    try {
                        await app.$store.dispatch(
                            'auth/fetchRefreshToken',
                            refreshToken
                        );
                    } catch (e) {
                        clearInterval(this.refreshTokenInterval);
                    }
                } else {
                    clearInterval(this.refreshTokenInterval);
                }
            }, REFRESH_TOKEN_INTERVAL_TIME);
        },
        prepareRouteToLogin,
        generateReferralLink(referralCode) {
            return `${process.env.VUE_APP_KH_SITE}/?register=true&ref=${referralCode}`;
        },
        generateSoftStatusKey() {
            return nanoid(SOFT_STATUS_KEY_LENGTH);
        },
        formatBytes(num, options = {}) {
            return bytes.format(num, {
                decimalPlaces: 2,
                unitSeparator: ' ',
                ...options,
            });
        },
        unescapeHTMLTextContent(str = '') {
            return he.decode(str);
        },
        openNewWindow(callback) {
            const tab = window.open('about:blank', 'blank', 'location=no');
            if (tab === null || tab === undefined) {
                app.$store.commit('message/setMessage', {
                    type: 'danger',
                    message: app.$t('message.disable_pop_up_blocker'),
                });
            } else {
                callback(tab);
            }
        },
        prepareWPOrgPluginIcon(icons = {}) {
            const defaultIcon = require('@/assets/images/noimg.png');
            return (
                icons['1X'] ??
                icons['2X'] ??
                icons.svg ??
                icons.default ??
                defaultIcon
            );
        },
        getElementOffset(element) {
            let de = document.documentElement;
            let box = element.getBoundingClientRect();
            let top = box.top + window.pageYOffset - de.clientTop;
            let left = box.left + window.pageXOffset - de.clientLeft;
            return { top: top, left: left };
        },
        padNumber(number, length = 2) {
            return number.toString().padStart(length, '0');
        },
        /**
         * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/PluralRules/PluralRules
         */
        nthNumber(number) {
            const pr = new Intl.PluralRules('en-US', { type: 'ordinal' });
            const suffixes = new Map([
                ['one', 'st'],
                ['two', 'nd'],
                ['few', 'rd'],
                ['other', 'th'],
            ]);
            const rule = pr.select(number);
            const suffix = suffixes.get(rule);
            return `${number}${suffix}`;
        },
    };
};
